/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { css, StyleSheet } from 'aphrodite';
import { LoneIconButton, PopoverList } from '@blend/enterprise-components';
import { logout } from 'blb-api/auth';
import { BlendBridge } from 'blb-types/blendBridge';
import { MeContext } from 'blb-contexts/Me';
import { useStorageState } from 'react-storage-hooks';
import {
  isSecondaryAuthEnabled,
  isUsingSecondarySession,
  PRIMARY_AUTH_PRINCIPAL_LOCALSTORAGE_KEY,
  SECONDARY_AUTH_PRINCIPAL_LOCALSTORAGE_KEY,
  USE_SECONDARY_AUTH_LOCALSTORAGE_KEY,
  usePrimarySession,
  useSecondarySession,
} from 'blb-utils/secondaryAuth';
import { LocalStorage } from 'blb-utils/localStorage';
import { principalTypeDisplayName } from 'blb-utils/login';
import { getConfig } from 'blb-utils/config';
import { NavLinkButton } from 'blb-components/AuthenticatedApp/RON/SideBar/NavLinkButton';
import { getUserProfile } from 'blb-api/users';
import * as Sentry from '@sentry/browser';

export const styles = StyleSheet.create({
  spacer: {
    marginBottom: 16,
  },
});

export namespace UserIconButton {
  export interface Props {
    showFullName?: boolean;
  }
}

export const UserIconButton: React.FC<UserIconButton.Props> = ({ showFullName }) => {
  const me = useContext(MeContext);
  const history = useHistory();
  const location = useLocation();
  const { enableAutoAssignment, isProfileRequiredForSA } = getConfig();
  const [profile, setProfile] = useState<BlendBridge.API.UserProfile>();

  useEffect(() => {
    const loadUserProfile = async (principalId: string) => {
      try {
        const { data } = await getUserProfile(principalId);
        setProfile(data);
      } catch (error) {
        Sentry.captureException(error);
      }
    };
    if (showFullName && me?.principalId) {
      loadUserProfile(me.principalId);
    }
  }, [me]);

  const canViewProfile = (principalType: BlendBridge.PrincipalType) =>
    (isProfileRequiredForSA ? ['notary', 'ops_desk_agent', 'settlement_agent'] : ['notary', 'ops_desk_agent']).includes(
      principalType,
    );

  const canViewStats = (principalType: BlendBridge.PrincipalType) => ['ops_desk_agent'].includes(principalType);

  // use hooks here to re-render react component when localStorage gets updated
  useStorageState(localStorage, USE_SECONDARY_AUTH_LOCALSTORAGE_KEY);
  useStorageState(localStorage, PRIMARY_AUTH_PRINCIPAL_LOCALSTORAGE_KEY);
  useStorageState(localStorage, SECONDARY_AUTH_PRINCIPAL_LOCALSTORAGE_KEY);

  // getting the real localStorage object with the utility functions here because we want to support TTL and nested objects
  const useSecondaryAuth = isUsingSecondarySession();
  const primaryAuthPrincipal = LocalStorage.get(PRIMARY_AUTH_PRINCIPAL_LOCALSTORAGE_KEY);
  const secondaryAuthPrincipal = LocalStorage.get(SECONDARY_AUTH_PRINCIPAL_LOCALSTORAGE_KEY);

  const buttonConfigs: PopoverList.Props['buttonConfigs'] = [
    {
      id: 'logout',
      label: 'Sign out',
      onClick: logout,
    },
  ];

  if (canViewStats(me!.principalType) && location.pathname !== '/stats' && enableAutoAssignment) {
    buttonConfigs.unshift({
      id: 'statsDashboard',
      label: 'Stats Dashboard',
      onClick: () => history.push('/stats'),
    });
  }
  if (canViewProfile(me!.principalType) && location.pathname !== '/profile') {
    buttonConfigs.unshift({
      id: 'profile',
      label: 'Your Profile',
      onClick: () => history.push('/profile'),
    });
  }

  if (isSecondaryAuthEnabled()) {
    if (secondaryAuthPrincipal) {
      buttonConfigs.unshift({
        className: useSecondaryAuth ? 'status-active' : 'status-not-active',
        id: 'it-UserIconButton-SwitchToSecondaryAuthBtn',
        key: 'secondary-auth',
        label: principalTypeDisplayName[secondaryAuthPrincipal.principalType],
        onClick: () => {
          if (useSecondaryAuth) {
            // already using secondary
            return;
          }
          // switch to use secondary session auth
          useSecondarySession();
          // redirecting to home page so we can reload session user (GET /auth/me)
          window.location.href = '/';
        },
        ...(useSecondaryAuth && { icon: 'check' }),
      });
    } else {
      buttonConfigs.unshift({
        key: 'login-secondary',
        label: 'Log in to another account',
        onClick: () => history.push('/login?forSecondary=true'),
      });
    }

    if (primaryAuthPrincipal) {
      buttonConfigs.unshift({
        className: !useSecondaryAuth ? 'status-active' : 'status-not-active',
        id: 'it-UserIconButton-SwitchToPrimaryAuthBtn',
        key: 'primary-auth',
        label: principalTypeDisplayName[primaryAuthPrincipal.principalType],
        onClick: () => {
          if (!useSecondaryAuth) {
            // already using primary
            return;
          }
          // switch to use primary session auth
          usePrimarySession();
          // redirecting to home page so we can reload session user (GET /auth/me)
          window.location.href = '/';
        },
        ...(!useSecondaryAuth && { icon: 'check' }),
      });
    }
  }

  const variantIconOnly = (
    <LoneIconButton
      btnSize="sm"
      className={css(styles.spacer)}
      icon="person"
      iconSize={24}
      id="it-userIcon-btn"
      theme="light"
    />
  );
  const variantWide = (
    <div>
      <NavLinkButton
        iconType="mood"
        id={`it-userIcon-btn`}
        isActive={false}
        label={`${profile?.firstName || ''} ${profile?.lastName || ''}`}
      />
    </div>
  );
  return (
    <PopoverList anchorPosition="top-start" buttonConfigs={buttonConfigs}>
      {showFullName ? variantWide : variantIconOnly}
    </PopoverList>
  );
};
